import type { Instance, SnapshotIn, SnapshotOut } from "mobx-state-tree";
import { types } from "mobx-state-tree";

/**
 * UI state for the global building/project scope.
 *
 * Every page under `/buildings/:id/...` is scoped to either the whole building
 * or a single project (`building_projects`). The active scope is reflected in
 * the URL (`?project=<id>`) — the source of truth per render — while this store
 * carries it across the Projects-view navigations whose links don't spell it
 * out.
 *
 * **Deliberately not persisted.** The scope is in-memory only and
 * `useBuildingScope` clears it whenever the building renders in Building view,
 * so switching back to Projects view (or opening the app fresh) always forces
 * a project pick from the dropdown rather than silently reusing the last one.
 */

export const BuildingScopeStore = types
  .model("BuildingScopeStore", {
    /**
     * Selected project id per building, keyed by `buildingId` (string). The
     * absence of a key means whole-building scope, so the first-visit default
     * is naturally "whole building" with no special-casing.
     */
    selectedProjectByBuilding: types.map(types.number),
  })
  .views((self) => ({
    /** The selected project id for a building, or null for whole-building scope. */
    getProjectId(buildingId: string): number | null {
      return self.selectedProjectByBuilding.get(buildingId) ?? null;
    },
  }))
  .actions((self) => ({
    /** Scope the building to a specific project. */
    setScope(buildingId: string, projectId: number) {
      self.selectedProjectByBuilding.set(buildingId, projectId);
    },
    /** Clear the building back to whole-building scope. */
    clearScope(buildingId: string) {
      self.selectedProjectByBuilding.delete(buildingId);
    },
  }));

export type BuildingScopeStoreInstance = Instance<typeof BuildingScopeStore>;
export type BuildingScopeStoreSnapshotIn = SnapshotIn<typeof BuildingScopeStore>;
export type BuildingScopeStoreSnapshotOut = SnapshotOut<typeof BuildingScopeStore>;
